class CartRemoveButton extends HTMLElement {
  constructor () {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor () {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this._availabilityCache = new Map();
    this._locationInventoryCache = new Map();
    this._currentStoreName = '';
    this._currentStoreId = '';

    this.initPerItemFetch();

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .then(() => {
          this.initPerItemFetch();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .then(() => {
          this.initPerItemFetch();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        this.initPerItemFetch();
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  async initPerItemFetch() {
    const variantIdEls = this.querySelectorAll('.product-variant-id[data-product-variant-id]');
    if (!variantIdEls || variantIdEls.length === 0) return;

    // const storeInfoPromise = this.fetchCurrentStoreInfo();

    const tasks = Array.from(variantIdEls).map(async (el) => {
      const productVariantId = el.dataset.productVariantId;
      if (!productVariantId) return;

      const cartItemRow = el.closest('.cart-item');
      const data = await this.getProductAvailability(productVariantId);
      const locationInventory = this.extractLocationInventory(data);

      if (cartItemRow) {
        const blockFromCC = data?.data?.productVariant?.product?.block_from_click_collect?.value;
        if (blockFromCC !== undefined) {
          cartItemRow.dataset.blockClickCollect = blockFromCC;
        }
      }

      if (cartItemRow) {
        cartItemRow.dataset.locationInventory = JSON.stringify(locationInventory);
      }

    });

    await Promise.allSettled([
      // storeInfoPromise,
      ...tasks]);

    // this.renderIndicators();
  }

  extractLocationInventory(response) {
    try {
      if (this._locationInventoryCache.has(response)) return this._locationInventoryCache.get(response);
    } catch (_) {
    }

    const edges = response?.data?.productVariant?.inventoryItem?.inventoryLevels?.edges;
    if (!Array.isArray(edges)) return [];

    const result = edges
      .map((edge) => {
        const node = edge?.node;
        const location = node?.location;
        const quantities = Array.isArray(node?.quantities) ? node.quantities : [];
        const available = quantities.find((q) => q?.name === 'available');

        return {
          id: location?.id,
          name: location?.name,
          quantity: typeof available?.quantity === 'number' ? available.quantity : Number(available?.quantity ?? 0),
          warehouseEnable: location?.warehouseStockAvailability?.value === 'true',
          storeEnable: location?.storeStockAvailability?.value === 'true',
        };
      })
      .filter((x) => x.id && x.name);

    return result;
  }

  // async fetchCurrentStoreInfo() {
  //     try {
  //         const response = await fetch('/cart.json');
  //         const cart = await response.json();
  //         this._currentStoreName = cart.attributes?._pickup_store_name || '';
  //         this._currentStoreId = cart.attributes?._pickup_store_id || '';

  //         const storeNameEl = this.querySelector('.pickup-store-name');
  //         const storeIdEl = this.querySelector('.pickup-store-id');
  //         if (storeNameEl) storeNameEl.textContent = this._currentStoreName;
  //         if (storeIdEl) storeIdEl.textContent = this._currentStoreId;
  //     } catch (e) {
  //         console.error('Failed to fetch current store info:', e);
  //     }
  // }

  // renderIndicators() {
  //     const cartItemRows = this.querySelectorAll('.cart-item');
  //     const currentStoreId = this._currentStoreId;
  //     const currentStoreName = this._currentStoreName;
  //     const hasStoreSelected = !!(currentStoreId || currentStoreName);

  //     cartItemRows.forEach((item) => {
  //         const locationInventory = JSON.parse(item.dataset.locationInventory || '[]');
  //         const blockClickCollect = item.dataset.blockClickCollect === 'true';

  //         const quantityInput =
  //             item.querySelector('.quantity__input[data-index]') ||
  //             item.querySelector('.quantity__input');
  //         const lineQty = quantityInput ? parseInt(quantityInput.value, 10) : 1;


  //         const warehouseLocations = locationInventory.filter((loc) => loc.warehouseEnable);
  //         const bestWarehouseQty = Math.max(...warehouseLocations.map((loc) => loc.quantity), 0);
  //         const deliveryAvailable = bestWarehouseQty >= lineQty;


  //         let pickupStatus = 'no-store';
  //         let pickupLocationName = '';
  //         if (hasStoreSelected) {
  //             const storeLocation = locationInventory.find(
  //                 (loc) =>
  //                     loc.id === `gid://shopify/Location/${currentStoreId}` ||
  //                     loc.name === currentStoreName
  //             );
  //             const storeInventory = storeLocation ? storeLocation.quantity : 0;
  //             const isPickupAvailable =
  //                 !blockClickCollect && storeLocation && storeInventory >= lineQty;
  //             pickupStatus = isPickupAvailable ? 'available' : 'unavailable';
  //             pickupLocationName = (storeLocation && storeLocation.name) ? storeLocation.name : (currentStoreName || '');
  //         }

  //         /* --- Availability indicators disabled ---
  //         const deliveryEl = item.querySelector('.cart-item__delivery-indicator');
  //         const pickupEl = item.querySelector('.cart-item__pickup-indicator');

  //         if (deliveryEl) {
  //             const labelEl = deliveryEl.querySelector('.indicator-label');
  //             deliveryEl.classList.remove('available', 'unavailable');

  //             if (deliveryAvailable) {
  //                 deliveryEl.classList.add('available');
  //                 if (labelEl) labelEl.textContent = 'Delivery';
  //             } else {
  //                 deliveryEl.classList.add('unavailable');
  //                 if (labelEl) labelEl.textContent = 'Delivery';
  //             }
  //         }

  //         if (pickupEl) {
  //             const labelEl = pickupEl.querySelector('.indicator-label');
  //             pickupEl.classList.remove('available', 'unavailable');

  //             if (pickupStatus === 'no-store') {
  //                 pickupEl.classList.add('unavailable');
  //                 if (labelEl) labelEl.textContent = 'Set store to see status';
  //             } else if (pickupStatus === 'available') {
  //                 pickupEl.classList.add('available');
  //                 if (labelEl) labelEl.textContent = pickupLocationName ? `Click & Collect - ${pickupLocationName}` : 'Click & Collect';
  //             } else {
  //                 pickupEl.classList.add('unavailable');
  //                 if (labelEl) labelEl.textContent = pickupLocationName ? `Click & Collect - ${pickupLocationName}` : 'Click & Collect';
  //             }
  //         }
  //         --- End availability indicators disabled ---*/
  //     });
  // }

  // async refreshIndicators() {
  //     await this.fetchCurrentStoreInfo();
  //     this.renderIndicators();
  // }

  async getProductAvailability(productVariantId) {
    if (this._availabilityCache.has(productVariantId)) {
      return this._availabilityCache.get(productVariantId);
    }
    const queryId = this.dataset.availabilityQueryId;
    if (!queryId) {
      console.warn('Missing data-availability-query-id on cart element');
      return {};
    }
    const url = `/apps/datajet/task/${queryId}`;
    const payload = { productVariantId };
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      console.log('check fetching product variant availability', data);
      this._availabilityCache.set(productVariantId, data);
      return data;
    } catch (error) {
      console.error('Fetch Error:', error);
      return {};
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor () {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
